/**
 * Plugin Logger
 *
 * Provides scoped logging for plugins with levels and formatting.
 *
 * Features:
 * - Plugin-scoped logging (prefixes all logs with plugin ID)
 * - Log levels: debug, info, warn, error
 * - Structured error logging
 * - Console formatting with colors (in development)
 */

import { logger } from '../../../platform/utils/logger';
import { PluginLogger as IPluginLogger } from '@/lib/platform/types/plugins';

export class PluginLogger implements IPluginLogger {
  constructor(
    private pluginId: string,
    private minLevel: LogLevel = 'info'
  ) {}

  debug(message: string, ...args: unknown[]): void {
    if (this.shouldLog('debug')) {
      logger.debug(this.format('DEBUG', message), ...args);
    }
  }

  info(message: string, ...args: unknown[]): void {
    if (this.shouldLog('info')) {
      logger.info(this.format('INFO', message), ...args);
    }
  }

  warn(message: string, ...args: unknown[]): void {
    if (this.shouldLog('warn')) {
      logger.warn(this.format('WARN', message), ...args);
    }
  }

  error(message: string, error?: Error, ...args: unknown[]): void {
    if (this.shouldLog('error')) {
      const formattedMessage = this.format('ERROR', message);

      if (error) {
        logger.error(formattedMessage, error, ...args);
        if (error.stack) {
          logger.error('Stack trace:', error.stack);
        }
      } else {
        logger.error(formattedMessage, ...args);
      }
    }
  }

  /**
   * Set minimum log level
   */
  setLevel(level: LogLevel): void {
    this.minLevel = level;
  }

  /**
   * Get current log level
   */
  getLevel(): LogLevel {
    return this.minLevel;
  }

  private format(level: string, message: string): string {
    const timestamp = new Date().toISOString();
    return `[${timestamp}] [${this.pluginId}] [${level}] ${message}`;
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const currentLevelIndex = levels.indexOf(this.minLevel);
    const messageLevelIndex = levels.indexOf(level);

    return messageLevelIndex >= currentLevelIndex;
  }
}

type LogLevel = 'debug' | 'info' | 'warn' | 'error';
